import type { IModularBenefitBeneficiary } from '@ben/shared/types/modular-benefit';
import type { ReactNode } from 'react';
import { createContext, use, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import BeneficiaryForm from '../beneficiary-form';

interface BeneficiaryFormRequest {
  acceptedContactTypes?: string[];
  // Edits this record instead of creating a new one.
  beneficiaryId?: string;
  onSaved: (beneficiary: IModularBenefitBeneficiary) => void;
}

interface BeneficiaryFormTakeoverContextValue {
  open: (request: BeneficiaryFormRequest) => void;
  // Beneficiaries created or edited while on this page. The enrolment
  // payload is not refetched mid-enrolment (a refetch would drop the unsaved
  // selection), so this is where their display details come from.
  savedBeneficiaries: IModularBenefitBeneficiary[];
  // Records a beneficiary saved outside the form (a dependant nominated from
  // the picker); saving an id again replaces its row.
  saveBeneficiaryRow: (beneficiary: IModularBenefitBeneficiary) => void;
}

const BeneficiaryFormTakeoverContext =
  createContext<BeneficiaryFormTakeoverContextValue | null>(null);

export const useBeneficiaryFormTakeover =
  (): BeneficiaryFormTakeoverContextValue => {
    const context = use(BeneficiaryFormTakeoverContext);
    if (!context) {
      throw new Error(
        'useBeneficiaryFormTakeover must be used inside <BeneficiaryFormTakeover>'
      );
    }
    return context;
  };

// Shows the beneficiary form in place of the host page while a request is
// open. The host is hidden rather than unmounted so its state survives Exit.
const BeneficiaryFormTakeover = ({ children }: { children: ReactNode }) => {
  const [request, setRequest] = useState<BeneficiaryFormRequest | null>(null);
  const [savedBeneficiaries, setSavedBeneficiaries] = useState<
    IModularBenefitBeneficiary[]
  >([]);
  const triggerRef = useRef<HTMLElement | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const open = (nextRequest: BeneficiaryFormRequest) => {
    triggerRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setRequest(nextRequest);
  };

  const handleExit = () => setRequest(null);

  // Re-posting the same person returns the same record, and an edit returns
  // the record with new details: either way one row per id.
  const saveBeneficiaryRow = (beneficiary: IModularBenefitBeneficiary) =>
    setSavedBeneficiaries((current) =>
      current.some(({ id }) => id === beneficiary.id)
        ? current.map((row) => (row.id === beneficiary.id ? beneficiary : row))
        : [...current, beneficiary]
    );

  const handleSaved = (beneficiary: IModularBenefitBeneficiary) => {
    saveBeneficiaryRow(beneficiary);
    request?.onSaved(beneficiary);
    setRequest(null);
  };

  // The host is hidden, not unmounted, so the clicked button would keep focus
  // while invisible: move focus into the form on open and back to the trigger
  // on Exit.
  useEffect(
    function syncFocusWithTakeover() {
      if (request) {
        formRef.current?.focus();
      } else {
        triggerRef.current?.focus();
        triggerRef.current = null;
      }
    },
    [request]
  );

  return (
    <BeneficiaryFormTakeoverContext.Provider
      value={{ open, savedBeneficiaries, saveBeneficiaryRow }}
    >
      {request && (
        <div ref={formRef} tabIndex={-1}>
          <BeneficiaryForm
            acceptedContactTypes={request.acceptedContactTypes}
            beneficiaryId={request.beneficiaryId}
            onExit={handleExit}
            onSaved={handleSaved}
          />
        </div>
      )}
      <HostContent $hidden={request !== null}>{children}</HostContent>
    </BeneficiaryFormTakeoverContext.Provider>
  );
};

export default BeneficiaryFormTakeover;

// `display: contents` keeps this wrapper out of layout so the host page still
// sizes against the app shell exactly as it did before the takeover existed.
const HostContent = styled.div<{ $hidden: boolean }>`
  display: contents;

  ${({ $hidden }) =>
    $hidden &&
    css`
      display: none;
    `}
`;
